import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';

export interface CouponData {
  code: string;
  expiresAt: number;
}

@Injectable({
  providedIn: 'root'
})
export class CouponService {
  private readonly COUPON_STORAGE_KEY = 'musaffa_coupon';
  private readonly COUPON_EXPIRY_HOURS = 24; // 1 day

  constructor(
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  /**
   * Extract coupon parameter from URL, store it, and remove from URL
   */
  extractAndStoreCoupon(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    const couponCode = params.get('coupon');

    if (couponCode) {
      // Store the coupon with expiration
      this.storeCoupon(couponCode);

      // Remove coupon parameter from URL
      params.delete('coupon');

      // Update URL without the coupon parameter
      const newUrl = new URL(window.location.href);
      newUrl.search = params.toString();

      // Navigate to the new URL without the coupon parameter
      this.router.navigateByUrl(newUrl.pathname + newUrl.search, { replaceUrl: true });
    }
  }

  /**
   * Store coupon code in localStorage with expiration
   */
  private storeCoupon(code: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const couponData: CouponData = {
      code,
      expiresAt: Date.now() + (this.COUPON_EXPIRY_HOURS * 60 * 60 * 1000) // 24 hours from now
    };

    localStorage.setItem(this.COUPON_STORAGE_KEY, JSON.stringify(couponData));
  }

  /**
   * Get stored coupon code if it exists and is not expired
   */
  getStoredCoupon(): string | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    const storedData = localStorage.getItem(this.COUPON_STORAGE_KEY);
    if (!storedData) {
      return null;
    }

    try {
      const couponData: CouponData = JSON.parse(storedData);

      // Check if coupon has expired
      if (Date.now() > couponData.expiresAt) {
        this.clearStoredCoupon();
        return null;
      }

      return couponData.code;
    } catch (error) {
      console.error('Error parsing stored coupon data:', error);
      this.clearStoredCoupon();
      return null;
    }
  }

  /**
   * Clear stored coupon
   */
  clearStoredCoupon(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    localStorage.removeItem(this.COUPON_STORAGE_KEY);
  }

  /**
   * Check if coupon exists and is valid
   */
  hasValidCoupon(): boolean {
    return this.getStoredCoupon() !== null;
  }

  /**
   * Get coupon expiration time
   */
  getCouponExpiration(): Date | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    const storedData = localStorage.getItem(this.COUPON_STORAGE_KEY);
    if (!storedData) {
      return null;
    }

    try {
      const couponData: CouponData = JSON.parse(storedData);
      return new Date(couponData.expiresAt);
    } catch (error) {
      return null;
    }
  }
}
